import { useRef, useEffect, useEffect as useEffectState } from "react";
import { Row, Typography, Button, Select } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BackButton from "components/BackButton";
import { TextInput, TextEditor, ContestCandidateImageUpload } from "components/common";
import {
  getCandidateDetailRequest,
  updateCandidateRequest,
} from "providers/CandidateProvider/slice";
import { getContestListRequest } from "providers/ContestProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const { Option } = Select;

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required("Họ tên là bắt buộc"),
  contestNumber: Yup.string().required("Số báo danh là bắt buộc"),
  hometown: Yup.string().required("Quê quán là bắt buộc"),
  currentResidence: Yup.string().required("Nơi ở hiện tại là bắt buộc"),
  message: Yup.string().required("Thông điệp là bắt buộc"),
  introduction: Yup.string().required("Giới thiệu là bắt buộc"),
  informationDetail: Yup.string().required("Thông tin chi tiết là bắt buộc"),
  videoUrl: Yup.string()
    .url("URL video không hợp lệ"),
  images: Yup.array()
    .min(1, "Ít nhất 1 ảnh là bắt buộc")
    .required("Ảnh là bắt buộc"),
});

const CandidateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const formikRef = useRef();

  const candidateDetail = useSelector(
    (state) => state.candidateSlice.candidateDetail
  );
  const isLoading = useSelector((state) => state.candidateSlice.isLoading);

  useEffect(() => {
    console.log(id, "candidateId from URL");
    if (id) {
      dispatch(getCandidateDetailRequest(id));
    }
  }, [id, dispatch]);

  // Debug: Log candidate detail
  useEffect(() => {
    console.log("Candidate Detail:", candidateDetail);
  }, [candidateDetail]);

  const handleUpdateCandidate = (values) => {
    const candidateData = {
      fullName: values.fullName,
      contestNumber: values.contestNumber,
      hometown: values.hometown,
      currentResidence: values.currentResidence,
      message: values.message,
      introduction: values.introduction,
      informationDetail: values.informationDetail,
      images: values.images.map((img) =>
        typeof img === "string"
          ? { url: img }
          : img.url
            ? { url: img.url }
            : img
      ),
    };

    // Only add videoUrl if it has a value
    if (values.videoUrl && values.videoUrl.trim() !== "") {
      candidateData.videoUrl = values.videoUrl;
    }

    dispatch(
      updateCandidateRequest({
        candidateId: id,
        candidateData,
      })
    )
      .then(() => {
        Helper.toastr(
          "Cập nhật thí sinh thành công",
          "Thí sinh đã được cập nhật thành công",
          "success"
        );
        navigate(`/voting/candidate_list`, {
          state: { canGoBack: true },
          replace: true,
        });
      })
      .catch((error) => {
        console.error("Update candidate error:", error);
        Helper.toastr(
          "Cập nhật thí sinh thất bại",
          "Có lỗi xảy ra khi cập nhật thí sinh. Vui lòng thử lại.",
          "error"
        );
      });
  };

  const getInitialValues = () => {
    console.log("Getting initial values, candidateDetail:", candidateDetail);

    if (!candidateDetail || Object.keys(candidateDetail).length === 0) {
      return {
        fullName: "",
        contestNumber: "",
        hometown: "",
        currentResidence: "",
        message: "",
        introduction: "",
        informationDetail: "",
        videoUrl: "",
        images: [],
      };
    }

    const initialValues = {
      fullName: candidateDetail.fullName || "",
      contestNumber: candidateDetail.contestNumber || "",
      hometown: candidateDetail.hometown || "",
      currentResidence: candidateDetail.currentResidence || "",
      message: candidateDetail.message || "",
      introduction: candidateDetail.introduction || "",
      informationDetail: candidateDetail.informationDetail || "",
      videoUrl: candidateDetail.videoUrl || "",
      images: candidateDetail.images || [],
    };

    console.log("Initial values:", initialValues);
    return initialValues;
  };

  // Don't render form until we have candidate data
  if (!candidateDetail || Object.keys(candidateDetail).length === 0) {
    return (
      <div className="candidate-edit-wrapper">
        <Row className="candidate-edit-header" justify="start" align="middle">
          <BackButton />
          <Typography.Title level={4}>Chỉnh sửa thí sinh</Typography.Title>
        </Row>
        <div className="edit-form-wrapper">
          <div style={{ padding: "24px", textAlign: "center" }}>
            {isLoading
              ? "Đang tải dữ liệu..."
              : "Không tìm thấy dữ liệu thí sinh"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-edit-wrapper">
      <Row className="candidate-edit-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>Chỉnh sửa thí sinh</Typography.Title>
      </Row>
      <div className="edit-form-wrapper">
        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleUpdateCandidate}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="candidate-form-container">
                {/* Basic Information */}
                <div className="form-section">
                  <div className="section-title">Thông tin cơ bản</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Họ tên:</div>
                      <TextInput name="fullName" size="large" required />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Số báo danh:</div>
                      <TextInput name="contestNumber" size="large" required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Quê quán:</div>
                      <TextInput name="hometown" size="large" required />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Nơi ở hiện tại:</div>
                      <TextInput
                        name="currentResidence"
                        size="large"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="form-section">
                  <div className="section-title">Thông điệp</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thông điệp của thí sinh:</div>
                      <TextEditor
                        name="message"
                        placeholder="Nhập thông điệp của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Introduction */}
                <div className="form-section">
                  <div className="section-title">Giới thiệu</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Giới thiệu của thí sinh:</div>
                      <TextEditor
                        name="introduction"
                        placeholder="Nhập giới thiệu của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Information Detail */}
                <div className="form-section">
                  <div className="section-title">Thông tin chi tiết</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thông tin chi tiết của thí sinh:</div>
                      <TextEditor
                        name="informationDetail"
                        placeholder="Nhập thông tin chi tiết của thí sinh..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Video URL */}
                <div className="form-section">
                  <div className="section-title">Video</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">URL Video:</div>
                      <TextInput
                        name="videoUrl"
                        size="large"
                        placeholder="https://example.com/video.mp4"
                      />
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="form-section">
                  <div className="section-title">Ảnh thí sinh</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Ảnh thí sinh:</div>
                      <ContestCandidateImageUpload
                        name="images"
                        setFieldValue={setFieldValue}
                        required
                        maxImages={4}
                        formType="candidate"
                        helpText="Tải lên ảnh của thí sinh (tối đa 4 ảnh, khuyến nghị ảnh chân dung)"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => formikRef.current?.submitForm()}
                    loading={isLoading}
                  >
                    Cập nhật
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CandidateEdit;
